export interface Tenant {
  id: number | string;
  slug: string;
  name: string;
  status?: string | null;
  tenant_type?: string | null;
  setup_state?: string | null;
  provisioning_error?: string | null;
  health_status?: string | null;
  health_last_checked_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AttentionItem {
  tenant: Tenant;
  title: string;
  detail: string;
  severity: 'error' | 'warning' | 'info';
}

export function getSetupTenants(tenants: readonly Tenant[]): Tenant[] {
  return tenants.filter(t => t.status !== 'archived' && t.status !== 'suspended' &&
    ['provisioning', 'awaiting_verification', 'verified', 'failed'].includes(t.setup_state || ''));
}

// One item per tenant; a failed setup takes precedence over a health warning.
// Unknown health never becomes a claim that the tenant is healthy or down.
export function getAttentionItems(tenants: readonly Tenant[]): AttentionItem[] {
  const items: AttentionItem[] = [];
  for (const tenant of tenants) {
    if (tenant.status === 'archived' || tenant.status === 'suspended') continue;
    if (tenant.setup_state === 'failed' || tenant.provisioning_error) {
      items.push({ tenant, title: 'Setup needs attention', detail: 'Review the setup steps and reported error.', severity: 'error' });
    } else if (tenant.health_status === 'down' || tenant.health_status === 'degraded') {
      items.push({ tenant, title: `Last reported health: ${tenant.health_status}`, detail: tenant.health_last_checked_at ? `Checked ${formatObservedDate(tenant.health_last_checked_at)}` : 'Check time unavailable; open tenant to investigate.', severity: tenant.health_status === 'down' ? 'error' : 'warning' });
    } else if (tenant.setup_state === 'awaiting_verification') {
      items.push({ tenant, title: 'Ready for verification', detail: 'Open the tenant setup to verify access.', severity: 'warning' });
    } else if (tenant.setup_state === 'verified') {
      items.push({ tenant, title: 'Client invitation pending', detail: 'Review the client administrator setup step.', severity: 'info' });
    }
  }
  const rank = { error: 0, warning: 1, info: 2 };
  return items.sort((a, b) => rank[a.severity] - rank[b.severity] || a.tenant.slug.localeCompare(b.tenant.slug));
}

function timestamp(value?: string | null): number {
  if (!value) return 0;
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : 0;
}

export function getRecentTenants(tenants: readonly Tenant[], limit = 6): Tenant[] {
  return [...tenants].filter(t => t.status !== 'archived')
    .sort((a, b) => timestamp(b.created_at) - timestamp(a.created_at) || a.slug.localeCompare(b.slug))
    .slice(0, Math.max(0, limit));
}

export function formatObservedDate(value?: string | null): string {
  const time = timestamp(value);
  return time ? new Date(time).toLocaleString() : 'time unavailable';
}

export function setupLabel(state?: string | null): string {
  const labels: Record<string, string> = { provisioning: 'Provisioning', awaiting_verification: 'Awaiting verification', verified: 'Client invitation pending', failed: 'Setup failed', live: 'Setup complete' };
  return labels[state || ''] || 'Setup status unavailable';
}
